import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Share2, Download, MoreVertical, Plus, Type, Hash, List, Table, FileSignature, Calculator, CheckSquare, Slash, Edit3, Eye, Lock, ChevronRight, ChevronLeft, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TableBlock } from '../../components/blocks/TableBlock';
import { SignatureBlock } from '../../components/blocks/SignatureBlock';
import { FormFieldBlock } from '../../components/blocks/FormFieldBlock';
import { parseCommandInput, findCommandByPath, getAvailableCommands } from '../../components/blocks/commandRegistry';
import { useAppStore } from '../../store/app.store';
import { useAuthContext } from '../../hooks/useAuthContext';
import { loadInkDocument, saveInkDocument, publishInkDocument, shareInkDocument, requestSignature, createInkDocument } from '../../services/ink-document.service';
import { useInkAutoSave } from '../../hooks/useInkAutoSave';
const BLOCK_TYPES = [
    { type: 'paragraph', icon: Type, label: 'Text', description: 'Plain text paragraph' },
    { type: 'heading1', icon: Hash, label: 'Heading 1', description: 'Large section heading' },
    { type: 'heading2', icon: Hash, label: 'Heading 2', description: 'Medium section heading' },
    { type: 'heading3', icon: Hash, label: 'Heading 3', description: 'Small section heading' },
    { type: 'list', icon: List, label: 'Bulleted List', description: 'Create a simple list' },
    { type: 'table', icon: Table, label: 'Table', description: 'Add a table with data' },
    { type: 'signature', icon: FileSignature, label: 'Signature Block', description: 'Add signature field' },
    { type: 'formula', icon: Calculator, label: 'Formula', description: 'Calculate values' },
    { type: 'form-field', icon: CheckSquare, label: 'Form Field', description: 'Interactive form input' },
];
export function InkApp() {
    const navigate = useNavigate();
    const { documentType } = useParams();
    const { userProfile } = useAuthContext();
    const [documentTitle, setDocumentTitle] = useState('Untitled Document');
    const [documentMode, setDocumentMode] = useState('edit');
    const [blocks, setBlocks] = useState([
        { id: '1', type: 'paragraph', content: '', placeholder: 'Type / for commands...' }
    ]);
    const [documentMetadata, setDocumentMetadata] = useState({
        signatureRequests: []
    });
    const [showSlashMenu, setShowSlashMenu] = useState(false);
    const [slashMenuPosition, setSlashMenuPosition] = useState({ top: 0, left: 0 });
    const [activeBlockId, setActiveBlockId] = useState(null);
    const [slashSearch, setSlashSearch] = useState('');
    const [commandPath, setCommandPath] = useState([]);
    const [selectedCommand, setSelectedCommand] = useState(null);
    const [parameterValues, setParameterValues] = useState({});
    const [documentId, setDocumentId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const appStore = useAppStore();
    const { documentId: urlDocumentId } = useParams();
    // Auto-save hook
    const { isSaving, isDirty, lastSavedAt, saveNow } = useInkAutoSave({
        documentId: documentId || '', // Empty string if no document yet
        title: documentTitle,
        blocks,
        enabled: !!documentId && documentMode === 'edit', // Must have actual documentId
        debounceMs: 5000,
        onError: (error) => {
            console.error('Auto-save failed:', error);
            // Could show toast notification here
        }
    });
    // Load document on mount or when URL document ID changes
    useEffect(() => {
        const loadDocument = async () => {
            try {
                setIsLoading(true);
                // If URL has document ID, load from database
                if (urlDocumentId && urlDocumentId !== 'new') {
                    const loaded = await loadInkDocument(urlDocumentId);
                    if (loaded) {
                        setDocumentId(loaded.id);
                        setDocumentTitle(loaded.title);
                        setBlocks(loaded.blocks);
                        setDocumentMode(loaded.documentMode);
                    }
                    else {
                        console.error('Failed to load document');
                        // Could redirect to 404 here
                    }
                }
                else if (urlDocumentId === 'new' || !urlDocumentId) {
                    // Create new document in database
                    if (!userProfile?.organization_id) {
                        console.warn('[InkApp] Organization not found in user profile, waiting for auth...');
                        // Don't set loading to false - wait for userProfile
                        return;
                    }
                    console.log('[InkApp] Creating new document for org:', userProfile.organization_id);
                    const newDoc = await createInkDocument(userProfile.organization_id);
                    if (newDoc) {
                        console.log('[InkApp] New document created:', newDoc.id);
                        setDocumentId(newDoc.id);
                        setDocumentTitle(newDoc.title);
                        setBlocks(newDoc.blocks);
                        setDocumentMode('edit');
                    }
                    else {
                        console.error('[InkApp] Failed to create new document');
                        setDocumentId(null);
                        setDocumentTitle('Untitled Document');
                        setBlocks([{ id: '1', type: 'paragraph', content: '', placeholder: 'Type / for commands...' }]);
                    }
                }
            }
            catch (error) {
                console.error('Error loading document:', error);
            }
            finally {
                setIsLoading(false);
            }
        };
        loadDocument();
    }, [urlDocumentId, userProfile?.organization_id]);
    const handleBlockChange = (id, content) => {
        // Check if user typed /
        if (content.endsWith('/')) {
            const blockElement = document.getElementById(`block-${id}`);
            if (blockElement) {
                const rect = blockElement.getBoundingClientRect();
                setSlashMenuPosition({ top: rect.bottom, left: rect.left });
                setShowSlashMenu(true);
                setActiveBlockId(id);
                setSlashSearch('');
                setCommandPath([]);
            }
        }
        else if (showSlashMenu && content.includes('/')) {
            // Extract command input after /
            const slashIndex = content.lastIndexOf('/');
            const commandInput = content.substring(slashIndex);
            // Parse the command
            const parsed = parseCommandInput(commandInput);
            // Update command path for hierarchical navigation
            setCommandPath(parsed.path);
            // Use the last part as search term for filtering
            const lastPart = parsed.path[parsed.path.length - 1] || '';
            setSlashSearch(lastPart.toLowerCase());
            // If command is complete, execute it
            if (parsed.isComplete) {
                const command = findCommandByPath(parsed.path);
                if (command) {
                    executeCommand(id, command, parsed.parameters);
                }
            }
        }
        else if (showSlashMenu && !content.includes('/')) {
            setShowSlashMenu(false);
            setCommandPath([]);
        }
        setBlocks(blocks.map(block => block.id === id ? { ...block, content } : block));
    };
    const executeCommand = (blockId, command, parameters) => {
        if (!command.action)
            return;
        const currentBlock = blocks.find(b => b.id === blockId);
        if (!currentBlock)
            return;
        // Remove the slash command from content
        const slashIndex = currentBlock.content.lastIndexOf('/');
        const newContent = currentBlock.content.substring(0, slashIndex);
        switch (command.action.type) {
            case 'insert-block': {
                const blockType = command.action.blockType;
                const tableData = blockType === 'table' ? {
                    headers: ['Column 1', 'Column 2', 'Column 3'],
                    rows: [['', '', ''], ['', '', ''], ['', '', '']]
                } : undefined;
                setBlocks(blocks.map(block => block.id === blockId ? {
                    ...block,
                    content: newContent,
                    type: blockType,
                    tableData
                } : block));
                break;
            }
            case 'insert-signature': {
                const email = parameters[0] || '';
                const signatureData = {
                    signerName: email.split('@')[0] || '',
                    signerTitle: '',
                    isSigned: false
                };
                setBlocks(blocks.map(block => block.id === blockId ? {
                    ...block,
                    content: newContent,
                    type: 'signature',
                    signatureData
                } : block));
                // Track signature request in metadata
                if (email && command.action.data?.assignSignature) {
                    setDocumentMetadata(prev => ({
                        ...prev,
                        signatureRequests: [
                            ...prev.signatureRequests,
                            {
                                blockId,
                                assignee: email,
                                assigneeName: email.split('@')[0],
                                status: 'pending',
                                requestedAt: new Date().toISOString()
                            }
                        ]
                    }));
                }
                break;
            }
            case 'insert-form': {
                const fieldType = command.action.data?.fieldType || 'text';
                const label = parameters[0] || '';
                const formFieldData = {
                    label,
                    fieldType,
                    required: false,
                    value: ''
                };
                setBlocks(blocks.map(block => block.id === blockId ? {
                    ...block,
                    content: newContent,
                    type: 'form-field',
                    formFieldData
                } : block));
                break;
            }
            case 'fetch-data':
                // TODO: Implement data fetching
                console.log('Data fetch command:', parameters);
                break;
            case 'load-template':
                // TODO: Implement template loading
                console.log('Template load command:', parameters);
                break;
        }
        setShowSlashMenu(false);
        setSlashSearch('');
        setCommandPath([]);
    };
    const handleAddBlock = (afterId, type) => {
        const index = blocks.findIndex(b => b.id === afterId);
        const newBlock = {
            id: Date.now().toString(),
            type,
            content: '',
            placeholder: getPlaceholder(type)
        };
        const newBlocks = [
            ...blocks.slice(0, index + 1),
            newBlock,
            ...blocks.slice(index + 1)
        ];
        setBlocks(newBlocks);
        // Focus new block
        setTimeout(() => {
            const element = document.getElementById(`block-${newBlock.id}`);
            element?.focus();
        }, 50);
    };
    const handleDeleteBlock = (id) => {
        // Prevent deleting if it's the only block
        if (blocks.length <= 1)
            return;
        setBlocks(blocks.filter(block => block.id !== id));
        // Also remove from signature requests if it was a signature block
        setDocumentMetadata(prev => ({
            ...prev,
            signatureRequests: prev.signatureRequests.filter(req => req.blockId !== id)
        }));
    };
    const handleSlashCommand = (type) => {
        if (!activeBlockId)
            return;
        // Remove the / from the current block
        const currentBlock = blocks.find(b => b.id === activeBlockId);
        if (currentBlock) {
            const slashIndex = currentBlock.content.lastIndexOf('/');
            const newContent = currentBlock.content.substring(0, slashIndex);
            // Initialize data based on block type
            const tableData = type === 'table' ? {
                headers: ['Column 1', 'Column 2', 'Column 3'],
                rows: [
                    ['', '', ''],
                    ['', '', ''],
                    ['', '', '']
                ]
            } : undefined;
            const signatureData = type === 'signature' ? {
                signerName: '',
                signerTitle: '',
                isSigned: false
            } : undefined;
            const formFieldData = type === 'form-field' ? {
                label: '',
                fieldType: 'text',
                required: false,
                value: ''
            } : undefined;
            setBlocks(blocks.map(block => block.id === activeBlockId ? {
                ...block,
                content: newContent,
                type,
                tableData,
                signatureData,
                formFieldData
            } : block));
        }
        setShowSlashMenu(false);
        setSlashSearch('');
    };
    const handleKeyDown = (e, blockId) => {
        // Enter key - create new paragraph block
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddBlock(blockId, 'paragraph');
        }
        // Escape key - close slash menu
        if (e.key === 'Escape' && showSlashMenu) {
            setShowSlashMenu(false);
            setSlashSearch('');
        }
    };
    const getPlaceholder = (type) => {
        const placeholders = {
            paragraph: 'Type / for commands...',
            heading1: 'Heading 1',
            heading2: 'Heading 2',
            heading3: 'Heading 3',
            list: 'List item',
            table: 'Table (coming soon)',
            signature: 'Signature block (coming soon)',
            formula: 'Formula (coming soon)',
            'form-field': 'Form field (coming soon)'
        };
        return placeholders[type] || 'Type here...';
    };
    const renderBlock = (block) => {
        const isReadOnly = documentMode !== 'edit';
        const baseClasses = 'w-full outline-none resize-none';
        const commonProps = {
            id: `block-${block.id}`,
            value: block.content,
            onChange: (e) => handleBlockChange(block.id, e.target.value),
            onKeyDown: (e) => handleKeyDown(e, block.id),
            placeholder: block.placeholder,
            style: {
                color: 'var(--text-primary)',
                backgroundColor: 'transparent',
                border: 'none'
            },
            className: baseClasses
        };
        switch (block.type) {
            case 'heading1':
                return isReadOnly ? (_jsx("h1", { className: "text-4xl font-bold mb-4", style: { color: 'var(--text-primary)' }, children: block.content || '\u00A0' })) : (_jsx("input", { ...commonProps, className: `${baseClasses} text-4xl font-bold mb-4` }));
            case 'heading2':
                return isReadOnly ? (_jsx("h2", { className: "text-3xl font-semibold mb-3", style: { color: 'var(--text-primary)' }, children: block.content || '\u00A0' })) : (_jsx("input", { ...commonProps, className: `${baseClasses} text-3xl font-semibold mb-3` }));
            case 'heading3':
                return isReadOnly ? (_jsx("h3", { className: "text-2xl font-medium mb-2", style: { color: 'var(--text-primary)' }, children: block.content || '\u00A0' })) : (_jsx("input", { ...commonProps, className: `${baseClasses} text-2xl font-medium mb-2` }));
            case 'list':
                return (_jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { style: { color: 'var(--text-secondary)' }, children: "\u2022" }), isReadOnly ? (_jsx("p", { style: { color: 'var(--text-primary)' }, children: block.content || '\u00A0' })) : (_jsx("input", { ...commonProps, className: `${baseClasses} flex-1` }))] }));
            case 'table':
                if (!block.tableData)
                    return null;
                return (_jsx(TableBlock, { data: block.tableData, onChange: (newTableData) => {
                        setBlocks(blocks.map(b => b.id === block.id ? { ...b, tableData: newTableData } : b));
                    }, mode: documentMode }));
            case 'signature':
                if (!block.signatureData)
                    return null;
                return (_jsx(SignatureBlock, { data: block.signatureData, onChange: (newSignatureData) => {
                        setBlocks(blocks.map(b => b.id === block.id ? { ...b, signatureData: newSignatureData } : b));
                    }, mode: documentMode }));
            case 'formula':
                return (_jsxs("div", { className: "p-4 rounded-lg", style: { backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-color)' }, children: [_jsxs("div", { className: "flex items-center gap-2 mb-2", children: [_jsx(Calculator, { className: "w-5 h-5", style: { color: 'var(--text-secondary)' } }), _jsx("span", { style: { color: 'var(--text-secondary)' }, children: "Formula Block (Coming Soon)" })] }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-muted)' }, children: "Calculate values with formulas and functions" })] }));
            case 'form-field':
                if (!block.formFieldData)
                    return null;
                return (_jsx(FormFieldBlock, { data: block.formFieldData, onChange: (newFormFieldData) => {
                        setBlocks(blocks.map(b => b.id === block.id ? { ...b, formFieldData: newFormFieldData } : b));
                    }, mode: documentMode }));
            default:
                return isReadOnly ? (_jsx("p", { style: { color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }, children: block.content || '\u00A0' })) : (_jsx("textarea", { ...commonProps, className: `${baseClasses} min-h-[1.5rem]`, rows: 1, onInput: (e) => {
                        const target = e.target;
                        target.style.height = 'auto';
                        target.style.height = target.scrollHeight + 'px';
                    } }));
        }
    };
    // Get available commands based on current path
    const availableCommands = getAvailableCommands(commandPath);
    const filteredCommands = availableCommands.filter(command => slashSearch === '' ||
        command.label.toLowerCase().includes(slashSearch) ||
        command.description.toLowerCase().includes(slashSearch));
    // Group commands by category
    const groupedCommands = filteredCommands.reduce((acc, command) => {
        const category = command.category || 'blocks';
        if (!acc[category])
            acc[category] = [];
        acc[category].push(command);
        return acc;
    }, {});
    const categoryLabels = {
        blocks: 'BASIC BLOCKS',
        actions: 'ACTIONS',
        data: 'DATA',
        templates: 'TEMPLATES'
    };
    const handleCommandClick = (command) => {
        if (!activeBlockId)
            return;
        // If command has children, navigate to it
        if (command.children && command.children.length > 0) {
            setCommandPath(command.path);
            setSlashSearch('');
        }
        // If command has parameters, show parameter input UI
        else if (command.parameters && command.parameters.length > 0) {
            setSelectedCommand(command);
            // Initialize parameter values
            const initialValues = {};
            command.parameters.forEach(param => {
                initialValues[param.name] = '';
            });
            setParameterValues(initialValues);
        }
        // Otherwise execute the command
        else {
            executeCommand(activeBlockId, command, []);
        }
    };
    const handleExecuteWithParameters = () => {
        if (!activeBlockId || !selectedCommand)
            return;
        // Convert parameter values to array in order
        const params = selectedCommand.parameters?.map(param => parameterValues[param.name] || '') || [];
        executeCommand(activeBlockId, selectedCommand, params);
        setSelectedCommand(null);
        setParameterValues({});
    };
    const handleBackInMenu = () => {
        // If showing parameter input, go back to command list
        if (selectedCommand) {
            setSelectedCommand(null);
            setParameterValues({});
        }
        // Otherwise go up one level in command hierarchy
        else if (commandPath.length > 0) {
            setCommandPath(commandPath.slice(0, -1));
            setSlashSearch('');
        }
    };
    // Handler for publishing document
    const handlePublish = async () => {
        if (!documentId) {
            console.error('Document ID not set');
            return;
        }
        try {
            const published = await publishInkDocument(documentId);
            if (published) {
                setDocumentMode('view');
                console.log('Document published successfully');
                // Could show toast: "Document published"
            }
        }
        catch (error) {
            console.error('Error publishing document:', error);
            // Could show error toast
        }
    };
    // Handler for sharing document
    const handleShare = async (email, role = 'editor') => {
        if (!documentId) {
            console.error('Document ID not set');
            return;
        }
        try {
            const shared = await shareInkDocument(documentId, email, role);
            if (shared) {
                console.log(`Document shared with ${email}`);
                // Could show toast: "Document shared"
            }
        }
        catch (error) {
            console.error('Error sharing document:', error);
            // Could show error toast
        }
    };
    // Handler for requesting signature
    const handleRequestSignature = async (email, blockId) => {
        if (!documentId) {
            console.error('Document ID not set');
            return;
        }
        try {
            await requestSignature(documentId, blockId, email);
            console.log(`Signature requested from ${email}`);
            // Could show toast: "Signature request sent"
        }
        catch (error) {
            console.error('Error requesting signature:', error);
            // Could show error toast
        }
    };
    // Handler for manual save (in addition to auto-save)
    const handleManualSave = async () => {
        if (!documentId) {
            console.error('Document ID not set');
            return;
        }
        try {
            const saved = await saveInkDocument(documentId, documentTitle, blocks);
            if (saved) {
                console.log('Document saved manually');
                // Could show toast: "Saved"
            }
        }
        catch (error) {
            console.error('Error saving document:', error);
            // Could show error toast
        }
    };
    // Show loading state if document is being created
    if (isLoading && !documentId) {
        return (_jsx("div", { className: "flex flex-col h-full items-center justify-center", style: { backgroundColor: 'var(--bg-base)' }, children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" }), _jsx("p", { style: { color: 'var(--text-secondary)' }, children: "Creating your document..." })] }) }));
    }
    return (_jsxs("div", { className: "flex flex-col h-full", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsxs("div", { className: "flex items-center justify-between px-6 py-3 border-b", style: { borderColor: 'var(--border-color)' }, children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => navigate('/app/ink'), children: _jsx(ArrowLeft, { className: "w-4 h-4" }) }), _jsx("span", { className: "text-sm", style: { color: 'var(--text-muted)' }, children: documentType ? `${documentType} document` : 'Ink Editor' })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("div", { className: "flex items-center gap-1 px-1 py-1 rounded-lg", style: { backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-color)' }, children: [_jsxs("button", { onClick: () => setDocumentMode('edit'), className: `px-3 py-1 text-xs rounded flex items-center gap-1.5 transition-all ${documentMode === 'edit' ? 'font-medium' : ''}`, style: {
                                            backgroundColor: documentMode === 'edit' ? 'var(--bg-base)' : 'transparent',
                                            color: documentMode === 'edit' ? 'var(--text-primary)' : 'var(--text-secondary)'
                                        }, children: [_jsx(Edit3, { className: "w-3.5 h-3.5" }), "Edit"] }), _jsxs("button", { onClick: () => setDocumentMode('view'), className: `px-3 py-1 text-xs rounded flex items-center gap-1.5 transition-all ${documentMode === 'view' ? 'font-medium' : ''}`, style: {
                                            backgroundColor: documentMode === 'view' ? 'var(--bg-base)' : 'transparent',
                                            color: documentMode === 'view' ? 'var(--text-primary)' : 'var(--text-secondary)'
                                        }, children: [_jsx(Eye, { className: "w-3.5 h-3.5" }), "View"] }), _jsxs("button", { onClick: () => setDocumentMode('read'), className: `px-3 py-1 text-xs rounded flex items-center gap-1.5 transition-all ${documentMode === 'read' ? 'font-medium' : ''}`, style: {
                                            backgroundColor: documentMode === 'read' ? 'var(--bg-base)' : 'transparent',
                                            color: documentMode === 'read' ? 'var(--text-primary)' : 'var(--text-secondary)'
                                        }, children: [_jsx(Lock, { className: "w-3.5 h-3.5" }), "Read"] })] }), _jsx("div", { className: "w-px h-6", style: { backgroundColor: 'var(--border-color)' } }), _jsxs("div", { className: "flex items-center gap-3", children: [isSaving && (_jsxs("span", { className: "text-xs flex items-center gap-1", style: { color: 'var(--text-muted)' }, children: [_jsx("div", { className: "w-2 h-2 rounded-full bg-blue-500 animate-pulse" }), "Saving..."] })), !isSaving && isDirty && (_jsx("span", { className: "text-xs", style: { color: '#EF4444' }, children: "Unsaved changes" })), !isSaving && !isDirty && lastSavedAt && (_jsx("span", { className: "text-xs", style: { color: 'var(--text-muted)' }, children: "Saved" })), _jsx(Button, { variant: "outline", size: "sm", onClick: () => {
                                            console.log('[InkApp] Save button clicked, documentId:', documentId);
                                            saveNow();
                                        }, disabled: isSaving || !documentId, className: "flex items-center gap-2", children: isSaving ? 'Saving...' : 'Save' })] }), _jsx("div", { className: "w-px h-6", style: { backgroundColor: 'var(--border-color)' } }), _jsxs(Button, { variant: "ghost", size: "sm", className: "flex items-center gap-2", children: [_jsx(Share2, { className: "w-4 h-4" }), "Share"] }), _jsxs(Button, { variant: "ghost", size: "sm", className: "flex items-center gap-2", children: [_jsx(Download, { className: "w-4 h-4" }), "Export"] }), _jsx(Button, { variant: "ghost", size: "sm", children: _jsx(MoreVertical, { className: "w-4 h-4" }) })] })] }), _jsx("div", { className: "flex-1 overflow-y-auto", children: _jsxs("div", { className: "max-w-4xl mx-auto px-8 py-12", children: [documentMode === 'edit' ? (_jsx("input", { type: "text", value: documentTitle, onChange: (e) => setDocumentTitle(e.target.value), placeholder: "Untitled", className: "w-full text-5xl font-bold mb-12 outline-none", style: {
                                color: 'var(--text-primary)',
                                backgroundColor: 'transparent',
                                border: 'none'
                            } })) : (_jsx("h1", { className: "w-full text-5xl font-bold mb-12", style: { color: 'var(--text-primary)' }, children: documentTitle || 'Untitled' })), _jsx("div", { className: "space-y-4", children: blocks.map((block) => (_jsxs("div", { className: "group relative", children: [renderBlock(block), documentMode === 'edit' && (_jsxs("div", { className: "absolute -left-16 top-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1", children: [_jsx("button", { className: "p-1 rounded hover:bg-gray-100", onClick: () => handleAddBlock(block.id, 'paragraph'), title: "Add block below", children: _jsx(Plus, { className: "w-4 h-4", style: { color: 'var(--text-tertiary)' } }) }), blocks.length > 1 && (_jsx("button", { className: "p-1 rounded hover:bg-red-100", onClick: () => handleDeleteBlock(block.id), title: "Delete block", children: _jsx(Trash2, { className: "w-4 h-4", style: { color: '#EF4444' } }) }))] }))] }, block.id))) }), documentMode === 'edit' && (_jsx("div", { className: "mt-8 p-4 rounded-lg", style: { backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-color)' }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(Slash, { className: "w-5 h-5 mt-0.5", style: { color: 'var(--text-secondary)' } }), _jsxs("div", { children: [_jsx("h3", { className: "font-medium mb-1", style: { color: 'var(--text-primary)' }, children: "Type / for commands" }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Insert headings, tables, signatures, formulas, and more" })] })] }) }))] }) }), documentMode === 'edit' && showSlashMenu && (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 z-40", onClick: () => {
                            setShowSlashMenu(false);
                            setSlashSearch('');
                        } }), _jsx("div", { className: "fixed z-50 w-80 rounded-lg shadow-2xl overflow-hidden", style: {
                            top: slashMenuPosition.top + 4,
                            left: slashMenuPosition.left,
                            backgroundColor: 'var(--bg-elevated)',
                            border: '1px solid var(--border-color)',
                            maxHeight: '400px',
                            overflowY: 'auto'
                        }, children: _jsxs("div", { className: "p-2", children: [(commandPath.length > 0 || selectedCommand) && (_jsxs("div", { className: "px-3 py-2 flex items-center gap-2 border-b", style: { borderColor: 'var(--border-color)' }, children: [_jsx("button", { onClick: handleBackInMenu, className: "p-1 rounded hover:bg-opacity-50", style: { color: 'var(--text-secondary)' }, children: _jsx(ChevronLeft, { className: "w-4 h-4" }) }), _jsx("div", { className: "flex items-center gap-1 text-sm", style: { color: 'var(--text-secondary)' }, children: selectedCommand ? (_jsx(_Fragment, { children: selectedCommand.path.map((part, idx) => (_jsxs(React.Fragment, { children: [idx > 0 && _jsx(ChevronRight, { className: "w-3 h-3" }), _jsx("span", { children: part })] }, idx))) })) : (_jsx(_Fragment, { children: commandPath.map((part, idx) => (_jsxs(React.Fragment, { children: [idx > 0 && _jsx(ChevronRight, { className: "w-3 h-3" }), _jsx("span", { className: idx === commandPath.length - 1 ? 'font-medium' : '', children: part })] }, idx))) })) })] })), selectedCommand && selectedCommand.parameters && (_jsxs("div", { className: "p-3", children: [_jsxs("div", { className: "mb-3", children: [_jsx("div", { className: "font-medium mb-1", style: { color: 'var(--text-primary)' }, children: selectedCommand.label }), _jsx("div", { className: "text-sm", style: { color: 'var(--text-muted)' }, children: selectedCommand.description })] }), _jsx("div", { className: "space-y-3 mb-3", children: selectedCommand.parameters.map((param) => (_jsxs("div", { children: [_jsxs("label", { className: "block text-xs font-medium mb-1", style: { color: 'var(--text-secondary)' }, children: [param.name.charAt(0).toUpperCase() + param.name.slice(1), param.required && _jsx("span", { style: { color: '#EF4444' }, children: " *" })] }), _jsx("input", { type: param.type === 'email' ? 'email' : param.type === 'url' ? 'url' : 'text', value: parameterValues[param.name] || '', onChange: (e) => setParameterValues({
                                                            ...parameterValues,
                                                            [param.name]: e.target.value
                                                        }), placeholder: param.placeholder, className: "w-full px-3 py-2 text-sm rounded", style: {
                                                            backgroundColor: 'var(--bg-base)',
                                                            border: '1px solid var(--border-color)',
                                                            color: 'var(--text-primary)'
                                                        }, autoFocus: param === selectedCommand.parameters[0], onKeyDown: (e) => {
                                                            if (e.key === 'Enter') {
                                                                handleExecuteWithParameters();
                                                            }
                                                        } })] }, param.name))) }), _jsx("button", { onClick: handleExecuteWithParameters, disabled: selectedCommand.parameters.some(p => p.required && !parameterValues[p.name]), className: "w-full px-4 py-2 text-sm rounded font-medium transition-opacity", style: {
                                                backgroundColor: '#3B82F6',
                                                color: 'white',
                                                opacity: selectedCommand.parameters.some(p => p.required && !parameterValues[p.name]) ? 0.5 : 1,
                                                cursor: selectedCommand.parameters.some(p => p.required && !parameterValues[p.name]) ? 'not-allowed' : 'pointer'
                                            }, children: "Execute Command" })] })), !selectedCommand && Object.entries(groupedCommands).map(([category, commands]) => (_jsxs("div", { children: [_jsx("div", { className: "px-3 py-2 text-xs font-medium", style: { color: 'var(--text-muted)' }, children: categoryLabels[category] || category.toUpperCase() }), commands.map((command) => {
                                            const Icon = command.icon;
                                            const hasChildren = command.children && command.children.length > 0;
                                            return (_jsxs("button", { onClick: () => handleCommandClick(command), className: "w-full flex items-start gap-3 px-3 py-2 rounded hover:bg-opacity-50 transition-colors text-left", style: { backgroundColor: 'transparent' }, onMouseEnter: (e) => {
                                                    e.currentTarget.style.backgroundColor = 'var(--bg-base)';
                                                }, onMouseLeave: (e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }, children: [_jsx(Icon, { className: "w-5 h-5 mt-0.5 flex-shrink-0", style: { color: 'var(--text-secondary)' } }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: "font-medium", style: { color: 'var(--text-primary)' }, children: command.label }), _jsx("div", { className: "text-sm", style: { color: 'var(--text-muted)' }, children: command.description })] }), hasChildren && (_jsx(ChevronRight, { className: "w-4 h-4 mt-0.5 flex-shrink-0", style: { color: 'var(--text-tertiary)' } }))] }, command.id));
                                        })] }, category))), !selectedCommand && filteredCommands.length === 0 && (_jsx("div", { className: "px-3 py-4 text-center text-sm", style: { color: 'var(--text-muted)' }, children: "No commands found" }))] }) })] }))] }));
}
